import Dropdown from '@/components_v2/inputs/BaseDropdown';
import { Option } from '@/components_v2/inputs/BaseDropdown/interfaces';
import Tooltip from '@components/Tooltip';
import React from 'react';

import {
  InfoTooltip,
  QuestionContainer,
  QuestionHeader,
  QuestionSubtitle,
  QuestionTitle,
  TooltipContainer,
} from '../common';
import { CheckboxProps } from './interfaces';
import { useNestedFilterOptions } from './useNestedFilterOptions';

const Checkbox: React.FC<CheckboxProps> = ({
  question,
  errors,
  initialValues,
  onChange,
  viewOnly = false,
}) => {
  const { options: dynamicOptions, isDynamic } = useNestedFilterOptions(
    question,
    onChange,
  );
  const questionOptions = isDynamic ? dynamicOptions || [] : question.options;
  const options = questionOptions.map(({ id, name }) => ({
    value: id,
    label: name,
  }));

  // Get default value - handle both single value and array
  const getDefaultOptions = () => {
    const values = initialValues?.values;
    if (Array.isArray(values)) {
      return options.filter(({ value }) => values.includes(value));
    }
    if (values) {
      return options.filter(({ value }) => value === values);
    }
    return undefined;
  };

  // Create a key based on initial values to force re-mount when values change
  const dropdownKey = React.useMemo(() => {
    const values = initialValues?.values;
    if (Array.isArray(values)) {
      return values.join(',');
    }
    return values || 'empty';
  }, [initialValues?.values]);

  const [pageLimit, setPageLimit] = React.useState(50);
  const [inputValue, setInputValue] = React.useState('');

  const filteredOptions = React.useMemo(() => {
    if (!inputValue) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  }, [options, inputValue]);

  const loadMoreOptions = () => {
    setPageLimit((prev) => prev + 50);
  };

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    setPageLimit(50);
  };

  const onSubumit = (selected: Option[] | Option) => {
    if (!selected) return onChange([]);

    return Array.isArray(selected)
      ? onChange(selected.map(({ value }) => value))
      : onChange(selected.value);
  };

  return (
    <QuestionContainer>
      <QuestionHeader>
        <QuestionTitle>{question.name}</QuestionTitle>
        {question.tipText && (
          <Tooltip description={question.tipText} placement="top right">
            <TooltipContainer>
              <InfoTooltip />
            </TooltipContainer>
          </Tooltip>
        )}
      </QuestionHeader>
      <Dropdown
        key={dropdownKey}
        isMulti
        disabled={viewOnly}
        options={filteredOptions.slice(0, pageLimit)}
        onMenuScrollToBottom={loadMoreOptions}
        onInputChange={handleInputChange}
        hasError={!!errors}
        onChange={onSubumit}
        defaultValue={getDefaultOptions()}
      />
      <QuestionSubtitle hasError={!!errors}>
        {errors && errors.errors}
      </QuestionSubtitle>
    </QuestionContainer>
  );
};

export default Checkbox;
